<!-- app-subdomain-creators/src/Components/CreatorChatInlineVideoPlayer.vue -->
<template>
  <div
    class="relative w-[500px] rounded-[18px] overflow-hidden min-h-[280px] group"
    @mousemove="showUi"
    @mouseleave="hideUiDelayed"
  >
    <!-- Placeholder overlay while we haven't decided GIF vs thumb yet -->
    <div
      v-if="!hasPosterDecision"
      class="absolute inset-0 flex items-center justify-center bg-black/60 z-10 animate-pulse rounded-[18px]"
    >
      <div class="flex flex-col items-center gap-1">
        <div class="relative flex items-center justify-center">
          <div class="absolute inset-0 rounded-full border-4 border-[#2d8bff63]"></div>
          <div class="w-16 h-16 animate-spin rounded-full border-4 border-[#2D8CFF] border-t-transparent"></div>
        </div>
      </div>
    </div>

    <!-- Only show a text error if we really fail -->
    <div
      v-if="prepareError"
      class="absolute inset-0 flex items-center justify-center bg-black/70 z-20 px-4 rounded-[18px]"
    >
      <p class="text-xs text-red-200 text-center">
        {{ prepareError }}
      </p>
    </div>

    <!-- VIDEO ELEMENT (no native controls) -->
    <video
      ref="videoEl"
      class="w-[500px] h-auto transition-opacity duration-150 object-contain rounded-[18px]"
      playsinline
      preload="metadata"
      :poster="posterSrc"
      :class="hasPosterDecision ? 'opacity-100' : 'opacity-0'"
      @timeupdate="updateProgress"
      @loadedmetadata="setDuration"
      @loadeddata="onVideoLoaded"
      @click="togglePlay"
    >
      Your browser does not support the video tag. Try logging in with a different browser
    </video>

    <!-- BIG CENTER PLAY BUTTON (paused / not playing) -->
    <div
      v-if="isPaused && showOverlay && !isLoading && !prepareError && !isPreparing"
      @click.stop="togglePlay"
      class="absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity duration-300 rounded-[18px]"
    >
      <div
        class="flex items-center bg-[#2D8CFF] p-5 rounded-full hover:bg-[#2d8bffb2] opacity-80 transition"
      >
        <PlayIcon class="text-white/90 w-10 h-10 mx-auto pl-1" />
      </div>
    </div>

    <!-- LOADING OVERLAY (while HLS / video is loading) -->
    <transition name="loading-fade">
      <div
        v-if="isLoading && !prepareError"
        class="absolute inset-0 flex items-center justify-center bg-black/40 rounded-[18px]"
      >
        <div class="relative size-16">
          <div
            class="absolute inset-0 rounded-full border-[4px] border-opacity-20 border-[#2D8CFF]"
          ></div>
          <div
            class="size-16 animate-spin rounded-full border-[4px] border-[#2D8CFF] border-t-transparent"
          ></div>
        </div>
      </div>
    </transition>

    <!-- CONTROL BAR -->
    <transition name="fade">
      <div
        v-if="showControls && !isLoading && !prepareError && !isPreparing"
        class="absolute bottom-0 left-0 right-0 px-3 pt-6 pb-2 flex flex-col gap-2 rounded-[18px]"
        @mouseenter="onControlsEnter"
        @mouseleave="onControlsLeave"
      >
        <!-- Progress bar -->
        <div class="relative w-full">
          <!-- Hover preview bubble -->
          <transition name="fade">
            <div
              v-if="isHoveringBar"
              class="absolute -top-16"
              :style="{ left: hoverPercent + '%' }"
              style="transform: translateX(-50%);"
            >
              <div class="flex flex-col items-center">
                <div
                  class="w-32 h-16 bg-black/90 border border-white/10 rounded-lg flex items-center justify-center text-[11px] text-white/70"
                >
                  {{ hoverFormattedTime }}
                </div>
                <div
                  class="mt-1 px-2 py-0.5 rounded-full bg-black/90 text-[10px] text-white/80"
                >
                  {{ hoverFormattedTime }}
                </div>
              </div>
            </div>
          </transition>

          <!-- Progress bar track -->
          <div
            ref="progressBarEl"
            class="relative w-full h-1.5 bg-white/20 rounded-full cursor-pointer overflow-hidden"
            @mousemove="onBarMouseMove"
            @mouseleave="onBarMouseLeave"
            @mousedown.prevent="onBarMouseDown"
          >
            <!-- Hover indicator line -->
            <div
              v-if="isHoveringBar"
              class="absolute top-0 bottom-0 w-[2px] bg-white/40"
              :style="{ left: hoverPercent + '%' }"
            ></div>

            <!-- Played progress -->
            <div
              class="absolute top-0 left-0 h-1.5 rounded-full bg-[#2D8CFF] transition-all"
              :style="{ width: progress + '%' }"
            ></div>
          </div>
        </div>

        <!-- Bottom row controls -->
        <div
          class="flex items-center justify-between text-white text-[12px] mt-2 select-none"
        >
          <!-- LEFT SIDE -->
          <div class="flex items-center gap-2">
            <!-- PLAY / PAUSE -->
            <button
              @click="togglePlay"
              class="p-2 rounded-full bg-black/60 hover:bg-black/80 transition flex items-center justify-center"
            >
              <PlayIcon
                v-if="isPaused"
                class="text-white/90 w-6 h-6"
              />
              <PauseIcon
                v-else
                class="text-white/90 w-6 h-6"
              />
            </button>

            <!-- VOLUME GROUP -->
            <div class="flex items-center gap-2 px-2 py-1.5 rounded-full bg-black/60">
              <button
                @click="toggleMute"
                class="hover:text-[#2D8CFF] transition"
              >
                <SpeakerXMarkIcon
                  v-if="isMuted || volume === 0"
                  class="text-white/90 w-5 h-5"
                />
                <SpeakerWaveIcon
                  v-else
                  class="text-white/90 w-5 h-5"
                />
              </button>

              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                v-model.number="volume"
                @input="onVolumeChange"
                class="w-20 cursor-pointer accent-[#2D8CFF] bg-transparent border-none
                      focus:outline-none focus:ring-0 focus:border-transparent appearance-none"
                      :style="volumeTrackStyle"
              />


            </div>

            <!-- TIME DISPLAY -->
            <div class="px-2.5 py-1.5 rounded-full bg-black/60 text-[11px]">
              {{ formattedTime }} / {{ formattedDuration }}
            </div>
          </div>

          <!-- RIGHT SIDE -->
          <div class="flex items-center gap-2">
            <!-- FULLSCREEN -->
            <button
              @click="toggleFullscreen"
              class="p-2 rounded-full bg-black/60 hover:bg-black/80 transition flex items-center justify-center"
            >
              <ArrowsPointingOutIcon class="text-white/90 w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- LARGE PLATFORMA VIDEO PLAYER MODAL (media-drive style) -->
    <TransitionRoot as="template" :show="isLargePlayerOpen">
      <Dialog class="relative z-[9999]" @close="closeLargePlayer">
        <!-- Overlay -->
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div
            class="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm transition-opacity"
          ></div>
        </TransitionChild>

        <!-- FLOATING CONTROLS ON OVERLAY (TOP-LEFT) -->
        <div
          class="fixed top-4 left-4 z-[10000] flex items-center gap-3 text-white"
        >
          <!-- Close button -->
          <button
            @click="closeLargePlayer"
            class="h-9 w-9 flex items-center justify-center rounded-full
                   bg-black/40 hover:bg-black/60 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <!-- File icon + name pill -->
          <div
            class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur"
          >
            <!-- Always video icon for chat -->
            <div class="flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-zinc-200"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14m-10 4h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </div>

            <p class="text-xs font-medium text-zinc-100 truncate max-w-[260px]">
              {{ props.fileName || 'Chat video' }}
            </p>
          </div>
        </div>

        <!-- MODAL CONTENT (CENTERED PANEL) -->
        <div class="fixed inset-0 flex items-center justify-center p-4 z-[9999]">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="relative w-full overflow-hidden">
              <div class="flex items-center justify-center p-4">
                <div
                  class="w-[90vw] h-[90vh] bg-transparent rounded-xl overflow-hidden flex items-center justify-center"
                >
                  <!-- Same big player as Media Content Drive -->
                  <!-- NEW: tell PlatformaVideoPlayer to use chat HLS route -->
                  <PlatformaVideoPlayer :fileId="fileId" :chat-mode="true" />
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount, computed } from 'vue';
import Hls from 'hls.js';

import {
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/vue/24/solid';

import {
  Dialog,
  DialogPanel,
  TransitionRoot,
  TransitionChild,
} from '@headlessui/vue';

// Local "processing" GIF – shown while backend is still encoding / HLS missing
import processingPoster from '@/assets/media/images/chat-video-thumb.gif';

import PlatformaVideoPlayer from '@/Components/PlatformaVideoPlayer.vue';

// Same env you already use for master.m3u8 preflight
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const props = defineProps({
  fileId: {
    type: String,
    required: true,
  },
  // optional override poster if caller really has one already
  poster: {
    type: String,
    default: '',
  },
  // legacy direct mp4 src fallback
  src: {
    type: String,
    default: '',
  },
  // lets the big overlay show the original filename
  fileName: {
    type: String,
    default: '',
  },
});

const videoEl = ref(null);
let hlsInstance = null;

/* ────────────────────────────────────────────
   State (HLS / processing)
──────────────────────────────────────────── */

const isPreparing = ref(false); // backend still encoding
const isLoading   = ref(false); // generic load of a valid HLS
const prepareError = ref('');

// poster bound to <video poster="">
const posterSrc = ref('');

// "we've decided GIF vs thumbnail"
const hasPosterDecision = ref(false);

let retryTimer = null;
let retryCount = 0;
const MAX_RETRY = 50;
const RETRY_DELAY_MS = 15000;
let currentPlaylistUrl = '';

/* ────────────────────────────────────────────
   State (custom controls)
──────────────────────────────────────────── */

const isPaused = ref(true);
const showControls = ref(false);
const showOverlay = ref(true);

const duration = ref(0);
const currentTime = ref(0);
const volume = ref(1);
const isMuted = ref(false);
const lastNonZeroVolume = ref(1);

const progressBarEl = ref(null);
const isHoveringBar = ref(false);
const hoverPercent = ref(0);
const hoverFormattedTime = ref('0:00');

let hideUiTimeout = null;

const volumeTrackStyle = computed(() => ({
  '--volume-percent': `${volume.value * 100}%`,
}));

const isLargePlayerOpen = ref(false);
/* ────────────────────────────────────────────
   Logging
──────────────────────────────────────────── */

function log(...args) {
  console.log('[CreatorChatInlineVideoPlayer]', ...args);
}

/* ────────────────────────────────────────────
   Cleanup helpers
──────────────────────────────────────────── */

function clearRetryState() {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  retryCount = 0;
  isPreparing.value = false;
  prepareError.value = '';
}

function destroyHls() {
  if (hlsInstance) {
    try {
      log('destroyHls() – destroying previous Hls instance');
      hlsInstance.destroy();
    } catch (e) {
      console.warn('[CreatorChatInlineVideoPlayer] hls.destroy failed', e);
    }
    hlsInstance = null;
  }
  clearRetryState();
  isLoading.value = false;
}

function clearHideUiTimeout() {
  if (hideUiTimeout) {
    clearTimeout(hideUiTimeout);
    hideUiTimeout = null;
  }
}

/* ────────────────────────────────────────────
   Poster helpers
──────────────────────────────────────────── */

function setProcessingPoster() {
  posterSrc.value = processingPoster;
  hasPosterDecision.value = true;
}

function setPosterFromUrl(url) {
  if (!url) return;
  posterSrc.value = url;
  hasPosterDecision.value = true;
}

/**
 * Fetch thumbnail via the proxy route:
 *   GET /api/chat-videos/thumbnail/:fileId
 */
async function fetchAndSetThumbnail(fileId) {
  if (!fileId) return;

  // If caller gave us a static poster, just use that and skip fetch.
  if (props.poster) {
    log('[Thumb] using explicit poster from props:', props.poster);
    setPosterFromUrl(props.poster);
    return;
  }

  const base = apiProxyUrl || '';
  const thumbUrl = `${base}/api/chat-videos/thumbnail/${encodeURIComponent(
    fileId
  )}`;

  log('[Thumb] fetchAndSetThumbnail →', thumbUrl);

  try {
    const resp = await fetch(thumbUrl, {
      method: 'GET',
      credentials: 'include',
    });

    if (!resp.ok) {
      log('[Thumb] non-OK response', resp.status, resp.statusText);
      return;
    }

    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);

    log('[Thumb] fetched thumbnail OK, setting poster to blob URL');
    setPosterFromUrl(objectUrl);
  } catch (err) {
    console.error('[Thumb] fetchAndSetThumbnail error:', err);
  }
}

/* ────────────────────────────────────────────
   HLS master preflight (DECIDES: ready vs processing)
──────────────────────────────────────────── */

async function prepareHlsRequest(fileId) {
  if (!apiProxyUrl) {
    console.error(
      '[CreatorChatInlineVideoPlayer] VUE_APP_API_PROXY_URL is missing; cannot build HLS URL'
    );
    return { url: '', exists: false };
  }

  const url = `${apiProxyUrl}/api/chat-videos/master/${fileId}`;
  currentPlaylistUrl = url;

  log('prepareHlsRequest() →', url);

  try {
    const resp = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });

    if (resp.ok) {
      log('[MASTER preflight] 200 OK – treating as READY');
      return { url, exists: true };
    }

    if (resp.status === 404) {
      log('[MASTER preflight] 404 – treating as PROCESSING');
      return { url, exists: false };
    }

    log('[MASTER preflight] non-OK status', resp.status);
    return { url, exists: false };
  } catch (err) {
    console.error(
      '[CreatorChatInlineVideoPlayer] Error prefetching HLS playlist:',
      err
    );
    return { url: '', exists: false };
  }
}

/* ────────────────────────────────────────────
   Retry for "still processing" manifest (Hls error path)
──────────────────────────────────────────── */

function scheduleRetry(hls) {
  if (retryCount >= MAX_RETRY) {
    log('Max retries reached; giving up on this video for now.');
    isPreparing.value = false;
    isLoading.value = false;
    prepareError.value =
      'Video is still processing. Please refresh the page.';
    destroyHls();
    return;
  }

  retryCount += 1;

  isPreparing.value = true;
  isLoading.value = false;
  setProcessingPoster();

  const attemptNum = retryCount;
  log(
    `Scheduling manifest retry #${attemptNum} in ${RETRY_DELAY_MS}ms`,
    { playlistUrl: currentPlaylistUrl }
  );

  if (retryTimer) clearTimeout(retryTimer);

  retryTimer = setTimeout(() => {
    if (!hls || !currentPlaylistUrl) return;
    log(`Retrying manifest load #${attemptNum} →`, currentPlaylistUrl);
    setProcessingPoster();
    hls.loadSource(currentPlaylistUrl);
  }, RETRY_DELAY_MS);
}

/* ────────────────────────────────────────────
   Preflight polling retry (before Hls)
──────────────────────────────────────────── */

async function schedulePreflightRetry(fileId) {
  if (retryCount >= MAX_RETRY) {
    log('Preflight: max retries reached; giving up on this video for now.');
    isPreparing.value = false;
    isLoading.value = false;
    prepareError.value =
      'Video is still processing. Please try again in a moment.';
    return;
  }

  retryCount += 1;

  isPreparing.value = true;
  isLoading.value = false;
  setProcessingPoster();

  const attemptNum = retryCount;
  log(
    `Preflight: scheduling retry #${attemptNum} in ${RETRY_DELAY_MS}ms`,
    { fileId, playlistUrl: currentPlaylistUrl }
  );

  if (retryTimer) clearTimeout(retryTimer);

  retryTimer = setTimeout(async () => {
    if (!videoEl.value) {
      log('Preflight retry aborted – no video element');
      return;
    }

    const { url, exists } = await prepareHlsRequest(fileId);

    if (!url) {
      log('Preflight retry: prepareHlsRequest returned empty URL; aborting');
      prepareError.value = 'Unable to prepare video stream.';
      isPreparing.value = false;
      isLoading.value = false;
      hasPosterDecision.value = true;
      return;
    }

    if (exists) {
      log('Preflight retry: master now READY, starting HLS');
      clearRetryState();
      isPreparing.value = false;
      isLoading.value = true;
      fetchAndSetThumbnail(fileId).catch(() => {});
      loadVideoSource(url);
    } else {
      schedulePreflightRetry(fileId);
    }
  }, RETRY_DELAY_MS);
}

/* ────────────────────────────────────────────
   Main loader (HLS or native HLS or fallback)
──────────────────────────────────────────── */

function loadVideoSource(playlistUrl) {
  const el = videoEl.value;
  if (!el || !playlistUrl) {
    log('loadVideoSource() – missing video element or playlistUrl', {
      hasVideoEl: !!el,
      playlistUrl,
    });
    return;
  }

  prepareError.value = '';
  isLoading.value = true;

  log('loadVideoSource() – using playlistUrl:', playlistUrl);

  // Safari / iOS native HLS
  if (el.canPlayType && el.canPlayType('application/vnd.apple.mpegurl')) {
    log('Native HLS supported, using video.src =', playlistUrl);

    const onCanPlay = () => {
      log('Native HLS canplay → backend ready');
      isLoading.value = false;
      isPreparing.value = false;
      prepareError.value = '';
      fetchAndSetThumbnail(props.fileId).catch(() => {});
      el.removeEventListener('canplay', onCanPlay);
    };

    el.addEventListener('canplay', onCanPlay);
    el.src = playlistUrl;
    return;
  }

  // Hls.js path
  if (Hls.isSupported()) {
    log('Hls.isSupported() → true, instantiating Hls');

    const hls = new Hls({
      enableWorker: true,
      lowLatencyMode: true,
      xhrSetup(xhr) {
        xhr.withCredentials = true;
      },
    });

    hlsInstance = hls;

    hls.on(Hls.Events.MANIFEST_LOADING, () => {
      log('HLS MANIFEST_LOADING', playlistUrl);
    });

    hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
      log('HLS MANIFEST_PARSED', {
        levels: data?.levels ? data.levels.length : 0,
      });

      isPreparing.value = false;
      isLoading.value = false;
      prepareError.value = '';

      fetchAndSetThumbnail(props.fileId).catch(() => {});
    });

    hls.on(Hls.Events.ERROR, (event, data) => {
      log('HLS ERROR', { event, data });

      // treat MANIFEST_LOAD_ERROR 404 as "still processing"
      if (
        data?.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR &&
        data?.response?.code === 404
      ) {
        log('HLS MANIFEST_LOAD_ERROR 404 – scheduling retry (still processing)');
        scheduleRetry(hls);
        return;
      }

      if (!data?.fatal) {
        return;
      }

      log('HLS fatal error, type:', data.type, 'details:', data.details);

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          log('HLS: trying to recover from other network error');
          hls.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          log('HLS: trying to recover from media error');
          hls.recoverMediaError();
          break;
        default:
          log('HLS: unrecoverable error, destroying instance');
          destroyHls();
          break;
      }
    });

    log('Hls.loadSource(...) + attachMedia(...)');
    hls.loadSource(playlistUrl);
    hls.attachMedia(el);
    return;
  }

  // No HLS support at all → fallback direct src
  log('No Hls.js or native HLS; falling back to direct src:', props.src);
  isLoading.value = false;
  isPreparing.value = false;
  prepareError.value = '';

  fetchAndSetThumbnail(props.fileId).catch(() => {});

  if (props.src) {
    el.src = props.src;
  }
}

/* ────────────────────────────────────────────
   Custom controls: helpers
──────────────────────────────────────────── */

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const progress = computed(() =>
  duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0
);

const formattedTime = computed(() => formatTime(currentTime.value));
const formattedDuration = computed(() => formatTime(duration.value));

function updateProgress() {
  const el = videoEl.value;
  if (!el) return;
  currentTime.value = el.currentTime || 0;
}

function setDuration() {
  const el = videoEl.value;
  if (!el) return;
  duration.value = Number.isFinite(el.duration) ? el.duration : 0;
}

function onVideoLoaded() {
  isLoading.value = false;
  // start paused, with overlay shown, no controls
  isPaused.value = elIsPaused();
  showOverlay.value = isPaused.value;
}

function elIsPaused() {
  const el = videoEl.value;
  if (!el) return true;
  return el.paused;
}

function togglePlay() {
  const el = videoEl.value;
  if (!el) return;

  if (el.paused) {
    el
      .play()
      .then(() => {
        isPaused.value = false;
        showOverlay.value = false;
        showControls.value = true;
        showUi(); // reset hide timer
      })
      .catch(err => {
        console.error('[CreatorChatInlineVideoPlayer] play() failed:', err);
      });
  } else {
    el.pause();
    isPaused.value = true;
    showOverlay.value = true;
    showControls.value = true;
  }
}

function toggleMute() {
  const el = videoEl.value;
  if (!el) return;

  if (isMuted.value || volume.value === 0) {
    const restore = lastNonZeroVolume.value || 0.5;
    volume.value = restore;
    el.volume = restore;
    el.muted = false;
    isMuted.value = false;
  } else {
    lastNonZeroVolume.value = volume.value || 0.5;
    volume.value = 0;
    el.volume = 0;
    el.muted = true;
    isMuted.value = true;
  }
}

function onVolumeChange() {
  const el = videoEl.value;
  if (!el) return;

  el.volume = volume.value;
  if (volume.value === 0) {
    isMuted.value = true;
    el.muted = true;
  } else {
    isMuted.value = false;
    el.muted = false;
    lastNonZeroVolume.value = volume.value;
  }
}

/* Progress bar interactions */

function barPercentFromEvent(event) {
  const bar = progressBarEl.value;
  if (!bar) return 0;
  const rect = bar.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const pct = Math.min(100, Math.max(0, (x / rect.width) * 100));
  return pct;
}

function onBarMouseMove(event) {
  isHoveringBar.value = true;
  const pct = barPercentFromEvent(event);
  hoverPercent.value = pct;

  if (duration.value > 0) {
    const time = (pct / 100) * duration.value;
    hoverFormattedTime.value = formatTime(time);
  } else {
    hoverFormattedTime.value = '0:00';
  }
}

function onBarMouseLeave() {
  isHoveringBar.value = false;
}

function onBarMouseDown(event) {
  const pct = barPercentFromEvent(event);
  if (duration.value <= 0) return;
  const el = videoEl.value;
  if (!el) return;

  const newTime = (pct / 100) * duration.value;
  el.currentTime = newTime;
  currentTime.value = newTime;
}

/* Fullscreen */

function toggleFullscreen() {
  // Pause inline player so audio doesn't double-play
  const el = videoEl.value;
  if (el && !el.paused) {
    el.pause();
    isPaused.value = true;
    showOverlay.value = true;
    showControls.value = true;
  }

  // Open PlatformaVideoPlayer overlay
  isLargePlayerOpen.value = true;
}

function closeLargePlayer() {
  isLargePlayerOpen.value = false;
}


/* UI visibility */

function showUi() {
  showControls.value = true;
  if (!isPaused.value) {
    showOverlay.value = false;
  }
  clearHideUiTimeout();
  hideUiTimeout = setTimeout(() => {
    if (!isPaused.value) {
      showControls.value = false;
      showOverlay.value = false;
    } else {
      // paused: hide controls, keep big play overlay
      showControls.value = false;
      showOverlay.value = true;
    }
  }, 2500);
}

function hideUiDelayed() {
  clearHideUiTimeout();
  hideUiTimeout = setTimeout(() => {
    if (!isPaused.value) {
      showControls.value = false;
      showOverlay.value = false;
    } else {
      showControls.value = false;
      showOverlay.value = true;
    }
  }, 500);
}

function onControlsEnter() {
  clearHideUiTimeout();
}

function onControlsLeave() {
  hideUiDelayed();
}

/* ────────────────────────────────────────────
   Watch fileId + video element
──────────────────────────────────────────── */

watch(
  [() => props.fileId, videoEl],
  async ([id, video]) => {
    log('watch(fileId, videoEl) fired', { id, hasVideo: !!video });

    if (!id || !video) return;

    destroyHls();
    clearRetryState();

    prepareError.value = '';
    isLoading.value = false;
    isPreparing.value = false;
    hasPosterDecision.value = false;
    posterSrc.value = '';

    // reset UI state for new video
    isPaused.value = true;
    showOverlay.value = true;
    showControls.value = false;
    duration.value = 0;
    currentTime.value = 0;

    const { url, exists } = await prepareHlsRequest(id);

    if (!url) {
      log('prepareHlsRequest returned empty URL; aborting load');
      prepareError.value = 'Unable to prepare video stream.';
      isLoading.value = false;
      isPreparing.value = false;
      hasPosterDecision.value = true;
      return;
    }

    if (exists) {
      // READY → start HLS immediately
      isPreparing.value = false;
      isLoading.value = true;
      fetchAndSetThumbnail(id).catch(() => {});
      loadVideoSource(url);
    } else {
      // PROCESSING → GIF + preflight polling
      isPreparing.value = true;
      isLoading.value = false;
      setProcessingPoster();
      schedulePreflightRetry(id);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  log('onBeforeUnmount – cleaning up');
  destroyHls();
  clearHideUiTimeout();
});
</script>

<style scoped>
.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: opacity 0.2s ease;
}
.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ─────────────────────────────────────────────
   Custom <input type="range"> styling
   (used for volume in the chat video player)
────────────────────────────────────────────── */

/* Base range reset */
input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
}

/* WebKit track with dynamic fill using CSS variable */
input[type='range']::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    #2d8cff 0%,
    #2d8cff var(--volume-percent, 50%),
    rgba(255, 255, 255, 0.25) var(--volume-percent, 50%),
    rgba(255, 255, 255, 0.25) 100%
  );
}

/* WebKit thumb */
input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background: #2d8cff;
  margin-top: -4px; /* centers thumb on 4px track */
}

/* Firefox track */
input[type='range']::-moz-range-track {
  height: 4px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.25);
}

/* Firefox filled portion */
input[type='range']::-moz-range-progress {
  height: 4px;
  border-radius: 9999px;
  background: #2d8cff;
}

/* Firefox thumb */
input[type='range']::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background: #2d8cff;
  border: none;
}
</style>





